import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["preview", "backgroundValue", "textValue", "contrastDot", "contrastStatus"];
  static values = {
    logoUrl: String,
    orgName: String,
    website: String,
    location: String,
    country: String,
    accessibleContrast: String,
    lowContrast: String,
    previewCompany: String,
    previewGreeting: String,
    previewMessage: String,
    previewAction: String,
    previewSignoff: String
  };

  connect() {
    this.updatePreview();
  }

  updatePreview() {
    if (!this.hasPreviewTarget) return;

    const backgroundColor = this.element.querySelector("#button_background_color")?.value || "#111827";
    const textColor = this.element.querySelector("#button_text_color")?.value || "#ffffff";

    if (this.hasBackgroundValueTarget) this.backgroundValueTarget.textContent = backgroundColor;
    if (this.hasTextValueTarget) this.textValueTarget.textContent = textColor;

    this.updateContrast(backgroundColor, textColor);
    this.previewTarget.innerHTML = this.previewHtml(backgroundColor, textColor);
  }

  updateContrast(backgroundColor, textColor) {
    if (!this.hasContrastStatusTarget) return;

    const ratio = this.contrastRatio(backgroundColor, textColor);
    const passes = ratio >= 4.5;
    this.contrastStatusTarget.textContent = passes
      ? this.interpolate(this.accessibleContrastValue, { ratio: ratio.toFixed(1) })
      : this.interpolate(this.lowContrastValue, { ratio: ratio.toFixed(1) });
    this.contrastStatusTarget.className = passes
      ? "font-medium text-emerald-700 dark:text-emerald-400"
      : "font-medium text-amber-700 dark:text-amber-400";

    if (this.hasContrastDotTarget) {
      this.contrastDotTarget.className = `size-2 rounded-full ${passes ? "bg-emerald-500" : "bg-amber-500"}`;
    }
  }

  contrastRatio(firstColor, secondColor) {
    const first = this.relativeLuminance(firstColor);
    const second = this.relativeLuminance(secondColor);
    return (Math.max(first, second) + 0.05) / (Math.min(first, second) + 0.05);
  }

  relativeLuminance(color) {
    const channels = color.slice(1).match(/.{2}/g).map(channel => parseInt(channel, 16) / 255);
    const [red, green, blue] = channels.map(channel => channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4);
    return (0.2126 * red) + (0.7152 * green) + (0.0722 * blue);
  }

  previewHtml(backgroundColor, textColor) {
    const organization = this.escapeHtml(this.orgNameValue || this.previewCompanyValue);
    const greeting = this.escapeHtml(this.previewGreetingValue);
    const message = this.escapeHtml(this.interpolate(this.previewMessageValue, { organization: this.orgNameValue || this.previewCompanyValue }));
    const action = this.escapeHtml(this.previewActionValue);
    const signoff = this.escapeHtml(this.previewSignoffValue);
    const logo = this.logoUrlValue
      ? `<img src="${this.escapeHtml(this.logoUrlValue)}" alt="${organization}" class="mx-auto max-h-12 max-w-40 object-contain">`
      : `<div class="mx-auto flex size-12 items-center justify-center rounded-xl bg-slate-900 text-lg font-semibold text-white">${organization.charAt(0)}</div>`;
    const contact = [this.locationValue, this.countryValue, this.websiteValue].filter(Boolean).map(value => this.escapeHtml(value)).join(" · ");

    return `
      <div class="px-6 py-8 sm:px-8">
        <div class="border-b border-slate-200 pb-7 text-center">${logo}</div>
        <div class="py-8">
          <p class="text-base font-semibold text-slate-950">${greeting}</p>
          <p class="mt-3 text-sm leading-6 text-slate-600">${message}</p>
          <span class="mt-6 inline-flex rounded-lg px-4 py-2.5 text-sm font-semibold" style="background-color: ${backgroundColor}; color: ${textColor};">${action}</span>
          <p class="mt-6 text-sm leading-6 text-slate-600">${signoff}<br>${organization}</p>
        </div>
        <div class="border-t border-slate-200 pt-6 text-center">
          <p class="text-xs font-semibold text-slate-700">${organization}</p>
          ${contact ? `<p class="mt-1 text-xs text-slate-500">${contact}</p>` : ""}
        </div>
      </div>`;
  }

  escapeHtml(text) {
    const element = document.createElement("div");
    element.textContent = text;
    return element.innerHTML;
  }

  interpolate(template, values) {
    return Object.entries(values).reduce((result, [key, value]) => result.replaceAll(`%{${key}}`, value), template);
  }
}
